/**
 * AnnotatedScreenshot Component
 * Display screenshots with numbered annotation markers
 */

import React, { useState } from 'react';
import { cn } from '../../lib/utils';

export const AnnotatedScreenshot = ({
  imageUrl,
  placeholderTitle,
  annotations = [],
  className,
}) => {
  const [activeAnnotation, setActiveAnnotation] = useState(null);

  // If no image URL, show placeholder
  const isPlaceholder = !imageUrl;

  return (
    <div className={cn("space-y-4", className)}>
      {/* Image Container */}
      <div className="relative rounded-lg border border-border overflow-hidden bg-muted/30">
        {isPlaceholder ? (
          // Placeholder for screenshots to be added later
          <div className="aspect-video flex items-center justify-center bg-gradient-to-br from-muted/50 to-muted">
            <div className="text-center p-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-lg bg-muted-foreground/10 flex items-center justify-center">
                <svg className="w-8 h-8 text-muted-foreground/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-sm font-medium text-muted-foreground">
                {placeholderTitle || 'Screenshot placeholder'}
              </p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                Annotated screenshot will be added here
              </p>
            </div>
          </div>
        ) : (
          // Actual image with annotation markers
          <div className="relative">
            <img 
              src={imageUrl} 
              alt={placeholderTitle || 'Annotated screenshot'}
              className="w-full"
            />
            
            {/* Annotation Markers */}
            {annotations.map((annotation) => (
              <button
                key={annotation.id}
                onClick={() => setActiveAnnotation(
                  activeAnnotation === annotation.id ? null : annotation.id
                )}
                className={cn(
                  "absolute w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold transition-all",
                  "border-2 shadow-lg",
                  activeAnnotation === annotation.id
                    ? "bg-primary text-primary-foreground border-primary scale-110"
                    : "bg-white text-primary border-primary/50 hover:scale-110"
                )}
                style={{
                  left: `${annotation.x}%`,
                  top: `${annotation.y}%`,
                  transform: 'translate(-50%, -50%)',
                }}
                aria-label={`Annotation ${annotation.id}: ${annotation.title}`}
              >
                {annotation.id}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Annotation Legend */}
      {annotations.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {annotations.map((annotation) => (
            <div
              key={annotation.id}
              className={cn(
                "flex items-start gap-3 p-3 rounded-lg border transition-all cursor-pointer",
                activeAnnotation === annotation.id
                  ? "bg-primary/5 border-primary"
                  : "bg-muted/30 border-border hover:bg-muted/50"
              )}
              onClick={() => setActiveAnnotation(
                activeAnnotation === annotation.id ? null : annotation.id
              )}
            >
              <div className={cn(
                "w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0",
                activeAnnotation === annotation.id
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted-foreground/20 text-muted-foreground"
              )}>
                {annotation.id}
              </div>
              <div>
                <h4 className="text-sm font-medium">{annotation.title}</h4>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {annotation.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Pre-defined annotation sets for each major page
// These will be populated once real screenshots are available
export const pageAnnotations = {
  dealDetail: {
    placeholderTitle: 'Deal Detail Page',
    annotations: [
      { id: 1, x: 15, y: 10, title: 'Deal Header', description: 'Deal name, stage badge, and quick action buttons' },
      { id: 2, x: 30, y: 35, title: 'Key Metrics', description: 'ROI, profit, break-even, and risk score at a glance' },
      { id: 3, x: 70, y: 25, title: 'AI Intelligence', description: 'AI-generated scores and buy/pass recommendation' },
      { id: 4, x: 50, y: 60, title: 'Analysis Tabs', description: 'Navigate between Acquisition, Renovation, Exit, Comps, etc.' },
      { id: 5, x: 80, y: 70, title: 'Actions Panel', description: 'Stage transitions and deal operations' },
    ],
  },
  
  comparables: {
    placeholderTitle: 'Market Comparables Page',
    annotations: [
      { id: 1, x: 20, y: 15, title: 'Filter Bar', description: 'Filter by arrondissement, type, condition, price range' },
      { id: 2, x: 50, y: 10, title: 'Statistics Summary', description: 'Aggregate metrics for current filter selection' },
      { id: 3, x: 30, y: 50, title: 'Comparable Table', description: 'Sortable list with price/m², date, confidence' },
      { id: 4, x: 85, y: 40, title: 'Source Badge', description: 'Data source and verification status' },
      { id: 5, x: 70, y: 75, title: 'Verification Actions', description: 'Verify, exclude, or flag comparables' },
    ],
  },
  
  exitTracker: {
    placeholderTitle: 'Exit Tracker Page',
    annotations: [
      { id: 1, x: 25, y: 15, title: 'Active Listings', description: 'Properties currently on market with days listed' },
      { id: 2, x: 60, y: 20, title: 'Listing Performance', description: 'Views, inquiries, and showing metrics' },
      { id: 3, x: 40, y: 50, title: 'Offer Management', description: 'Track, analyze, and respond to offers' },
      { id: 4, x: 75, y: 60, title: 'Holding Costs', description: 'Daily cost of continued ownership' },
      { id: 5, x: 50, y: 80, title: 'Price Reduction Tools', description: 'Model impact of price adjustments' },
    ],
  },
  
  partnerScorecard: {
    placeholderTitle: 'Partner Scorecard Page',
    annotations: [
      { id: 1, x: 20, y: 15, title: 'Portfolio Overview', description: 'Total partners, average scores, tier distribution' },
      { id: 2, x: 50, y: 35, title: 'Partner Table', description: 'All partners with scores, metrics, and status' },
      { id: 3, x: 80, y: 25, title: 'Performance Score', description: 'Composite 0-100 score with breakdown' },
      { id: 4, x: 35, y: 65, title: 'Deal History', description: 'Partner\'s track record across all deals' },
      { id: 5, x: 70, y: 70, title: 'Recommendations', description: 'AI suggestions for partner relationship' },
    ],
  },
  
  aiIntelligence: {
    placeholderTitle: 'AI Intelligence Page',
    annotations: [
      { id: 1, x: 15, y: 15, title: 'Score Cards', description: 'Deal Score, Risk Score, and Confidence at a glance' },
      { id: 2, x: 50, y: 12, title: 'Recommendation', description: 'Buy/Negotiate/Pass with confidence level' },
      { id: 3, x: 30, y: 45, title: 'Score Breakdown', description: 'Detailed factors for each score component' },
      { id: 4, x: 70, y: 50, title: 'Attention Flags', description: 'Priority issues requiring attention' },
      { id: 5, x: 50, y: 80, title: 'AI Memo', description: 'Natural language deal summary and assessment' },
    ],
  },
};

export default AnnotatedScreenshot;
